<template>
  <div class="custom-chip-field">
    <div class="custom-chip-trigger" :class="{ tappable: displayOnly }" @click="onTriggerClick">
      <Chip
        class="custom-chip"
        :class="{ active: showActiveRing }"
        :chipValue="chipLabel"
        :disabled="false"
        :displayOnly="displayOnly"
        @updateActiveChip="toggleEditCustom"
      >
        <img src="@/assets/svg/chips/yellow.svg" />
        <span class="custom-chip-label">
          {{ $t(customChip ? '' : 'Custom') }}
        </span>
      </Chip>
    </div>

    <div class="custom-chip-input" v-if="editCustomChip" v-click-outside="toggleEditCustom">
      <div class="label">
        {{ $t('Maximum') }} <br />
        {{ currencySymbol }} {{ formatAmountWithKM(Number(maxBet), 0) }}
      </div>
      <div class="input">
        <input type="tel" v-model="customChip" @blur="validtateInput" @keydown="blockNonDigit" pattern="^[0-9]*$" />
      </div>
      <div class="label-right" @click.stop="toggleEditCustom">
        {{ $t('Save') }}
      </div>
    </div>
  </div>
</template>
<script>
import Chip from '@/components/bet-panel/chip';
import { mapGetters, mapActions } from 'vuex';
import { formatAmountWithKM, blockNonDigit } from '@/utils/helpers';
import { debounce } from 'lodash';
import { NumericInputType } from '@/utils/index.model';

/*
 * The custom bet chip: a chip that opens an amount field, clamped to the
 * member's per-fight limit and persisted through `general/updateCustomBetChip`.
 *
 * Two modes, because two products own the active chip differently:
 *  - default (cockfight): the inner Chip drives `bets/activeChip` itself, as it
 *    does for every other chip in that panel.
 *  - `display-only` (Tài Xỉu): the inner Chip is inert and the amount is
 *    reported through `select-chip`, so the host's own store stays the source
 *    of truth. Pass `activeChip` so the selected ring can be drawn.
 *
 * The amount field is absolutely positioned across the whole actions row, so
 * the host must be a positioned ancestor (`position: relative`) and this
 * component's root must not be one.
 */
export default {
  components: {
    Chip,
  },
  props: {
    activeChip: {
      type: [Number, String],
      default: 0,
    },
    displayOnly: {
      type: Boolean,
      default: false,
    },
    /*
     * Tài Xỉu passes the table's arena, whose chips come from
     * GET /arenas/:id/bet-chips and whose custom chip is saved back with
     * PATCH. Empty (cockfight, and every other caller) keeps the global
     * `config` behaviour unchanged -- that endpoint rejects a cockfight arena.
     */
    arenaId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      customChip: 0,
      editCustomChip: false,
    };
  },
  mounted() {
    this.updateActiveChip = debounce(this.updateActiveChip, 200);
    this.toggleEditCustom = debounce(this.toggleEditCustom, 500);
  },
  computed: {
    ...mapGetters({
      config: 'general/config',
      member: 'general/member',
      arenaBetChips: 'general/arenaBetChips',
      arenaCustomBetChip: 'general/arenaCustomBetChip',
      arenaBetChipLimits: 'general/arenaBetChipLimits',
    }),
    // What the backend has saved for this chip. For an arena it arrives after
    // the first paint, which is why the field watches it rather than only
    // reading it once in `mounted`.
    savedCustomChip() {
      return this.arenaId ? this.arenaCustomBetChip(this.arenaId) : Number(this.config.customBetChip) || 0;
    },
    /*
     * The ceiling. An arena publishes its own `max_bet_chip`, which is not the
     * member's global per-fight limit -- clamping to the latter silently
     * rewrote amounts the arena would have accepted.
     */
    maxBet() {
      return this.arenaBetChipLimits(this.arenaId).max || Number(this.config.mainLimitAmountPerFight) || 0;
    },
    currencySymbol() {
      return this.member.currencySymbol;
    },
    chipLabel() {
      return this.customChip ? this.customChip : this.currencySymbol;
    },
    showActiveRing() {
      return this.displayOnly && !!this.customChip && Number(this.activeChip) === Number(this.customChip);
    },
    minBet() {
      if (this.arenaId) {
        return this.arenaBetChipLimits(this.arenaId).min || this.arenaBetChips(this.arenaId)[0] || 0;
      }
      return this.config.betChips[0] || 0;
    },
  },
  methods: {
    ...mapActions('bets', ['updateActiveChip']),
    ...mapActions('general', ['updateCustomBetChip', 'updateArenaCustomBetChip']),
    formatAmountWithKM,
    blockNonDigit($event) {
      blockNonDigit($event, NumericInputType.int);
    },
    onTriggerClick() {
      // A display-only Chip takes no pointer events, so the chip's own wrapper
      // is the trigger. It must not be the component root: the amount field
      // lives there too, and every tap inside it would toggle and re-validate.
      if (this.displayOnly) this.toggleEditCustom();
    },
    selectCustomChip(value) {
      this.$emit('select-chip', Number(value));
      if (!this.displayOnly) this.updateActiveChip(value);
    },
    toggleEditCustom() {
      this.validtateInput();
      this.$nextTick(() => {
        if (this.editCustomChip) {
          if (this.arenaId) {
            this.updateArenaCustomBetChip({ arenaId: this.arenaId, value: Number(this.customChip) });
          } else {
            this.updateCustomBetChip(Number(this.customChip));
          }
        }
        this.editCustomChip = !this.editCustomChip;
        this.selectCustomChip(this.customChip);
      });
    },
    validtateInput() {
      if (Number(this.customChip) < Number(this.minBet)) this.customChip = this.minBet;
    },
  },
  watch: {
    /*
     * Adopt whatever the backend has for this chip -- on first load the arena's
     * value arrives after mount, and switching arenas swaps it. Skipped while
     * the field is open so it cannot overwrite what is being typed.
     */
    savedCustomChip: {
      immediate: true,
      handler(value) {
        if (this.editCustomChip) return;
        if (Number(value) !== Number(this.customChip)) this.customChip = Number(value) || 0;
      },
    },
    customChip: {
      handler(newVal, oldVal) {
        if (newVal && isNaN(Number(newVal) || newVal.includes('.'))) {
          this.customChip = oldVal;
          return;
        }
        if (this.maxBet && Number(newVal) > Number(this.maxBet)) {
          this.customChip = Number(this.maxBet);
        }
        this.selectCustomChip(this.customChip);
      },
    },
  },
};
</script>
<style lang="scss" scoped>
.custom-chip-field {
  display: flex;
  justify-content: center;
  // Deliberately not positioned: the amount field below anchors to the host row.
  position: static;
}

// Transparent to layout, so the Chip keeps sizing itself from `.chip-wrapper`.
.custom-chip-trigger {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;

  &.tappable {
    cursor: pointer;
  }
}

.custom-chip {
  z-index: 6;
  position: relative;
  display: flex;
  justify-content: center;
  text-transform: uppercase;

  &-label {
    position: absolute;
    bottom: -18px;
    font-size: 11px;
  }
}

.custom-chip-input {
  position: absolute;
  left: 0;
  top: -5px;
  height: 110%;
  border-radius: 8px;
  background-color: #000;
  padding: 24px 37% 24px 24px;
  display: flex;
  flex-direction: row;
  opacity: 0.98;
  width: 100%;
  box-sizing: border-box;
  font-size: 13px;
  z-index: 5;
  color: #c0c7d3;
  align-items: center;

  .label {
    margin-right: 5px;
    height: 35px;
    line-height: 1.1;
    display: flex;
    justify-content: center;
    align-items: center;
    white-space: nowrap;
  }

  .input {
    width: 100%;
    max-width: 280px;
    padding: 0 1rem;
    box-sizing: border-box;

    input {
      height: 35px;
      width: 100%;
      margin: auto;
      font-size: 20px;
      text-align: center;
      border-radius: 15px;
      border-color: transparent;
      outline-offset: 0px;
      outline: unset;
    }
  }

  .label-right {
    position: absolute;
    right: 30px;
    font-size: 15px;
    cursor: pointer;
  }
}
</style>
